import React, { useMemo } from 'react';

// iter94.12 — PartialDateInput.
//
// Families often know the YEAR of a life event but not the month or day
// (e.g., "graduated college in 1965"). The previous `<input type="date">`
// forced a full Y-M-D which blocked them. This component lets them
// supply whichever portion they know:
//
//   Year only         → "1965"
//   Year + Month      → "1965-03"
//   Year + Month + D  → "1965-03-15"
//
// Empty year returns "". Day is hidden until Month is set so we can't
// accidentally produce something like "1965--15".
//
// Backend stores all of these as plain strings (LifeEventIn.start_date
// is Optional[str]), and sort/display already do the right thing with
// partial values (lexicographic compare on YYYY[-MM[-DD]] is also
// chronologically correct).

const MONTHS = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function daysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

function parseValue(value) {
  if (!value) return { year: '', month: '', day: '' };
  // iter95.4 — Year segment is `\d{1,4}` so partial typed values (e.g.
  // "1", "19", "196") round-trip through the component while the user
  // is still typing. Previously this was `\d{4}` which forced the input
  // to clear on every keystroke until the 4th digit landed.
  const m = /^(\d{1,4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(String(value).trim());
  if (!m) return { year: '', month: '', day: '' };
  return {
    year: m[1] || '',
    month: m[2] ? String(Number(m[2])) : '',
    day: m[3] ? String(Number(m[3])) : '',
  };
}

function joinValue({ year, month, day }) {
  if (!year) return '';
  if (!month) return year;
  const mm = String(month).padStart(2, '0');
  if (!day) return `${year}-${mm}`;
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

export function PartialDateInput({
  value,
  onChange,
  yearMin = 1850,
  yearMax,
  testIdPrefix = 'partial-date',
  className = '',
}) {
  const max = yearMax ?? new Date().getFullYear();
  const parsed = useMemo(() => parseValue(value), [value]);
  const dayCount = daysInMonth(parsed.year, parsed.month);

  const update = (next) => onChange(joinValue({ ...parsed, ...next }));

  return (
    <div className={`grid grid-cols-[1.2fr_1fr_1fr] gap-2 ${className}`} data-testid={testIdPrefix}>
      <input
        type="text"
        inputMode="numeric"
        pattern="\d*"
        placeholder="Year"
        className="ml-input"
        value={parsed.year}
        maxLength={4}
        onChange={(e) => {
          const y = e.target.value.replace(/\D/g, '').slice(0, 4);
          // If year is cleared, blow away month + day too so we never
          // emit a `-03` orphan.
          if (!y) onChange('');
          else update({ year: y });
        }}
        onBlur={(e) => {
          // On blur, if the typed year is implausibly small (e.g. they
          // typed "19" and tabbed away), keep it as-is — the user can
          // come back. We do NOT auto-pad to 1900s because that would
          // silently rewrite "192" to "1920".
          const y = e.target.value.replace(/\D/g, '');
          if (y && y.length < 4) {
            // Visual hint via aria-invalid; no destructive clamping.
            e.target.setAttribute('aria-invalid', 'true');
          } else {
            e.target.removeAttribute('aria-invalid');
          }
        }}
        data-testid={`${testIdPrefix}-year`}
        aria-label={`Year (${yearMin}-${max})`}
      />
      <select
        className="ml-input"
        value={parsed.month}
        disabled={(parsed.year || '').length < 4}
        onChange={(e) => {
          const m = e.target.value;
          // Reset day if new month has fewer days than current day.
          const dayNum = Number(parsed.day);
          const nextDays = daysInMonth(parsed.year, m);
          update({ month: m, day: dayNum > nextDays ? '' : parsed.day });
        }}
        data-testid={`${testIdPrefix}-month`}
        aria-label="Month (optional)"
      >
        <option value="">Month —</option>
        {MONTHS.slice(1).map((label, idx) => (
          <option key={label} value={idx + 1}>{label}</option>
        ))}
      </select>
      <select
        className="ml-input"
        value={parsed.day}
        disabled={!parsed.month}
        onChange={(e) => update({ day: e.target.value })}
        data-testid={`${testIdPrefix}-day`}
        aria-label="Day (optional)"
      >
        <option value="">Day —</option>
        {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
    </div>
  );
}

// formatPartialDate — friendly display for stored YYYY[-MM[-DD]] strings.
//
//   "1965"        → "1965"
//   "1965-03"     → "Mar 1965"
//   "1965-03-15"  → "Mar 15, 1965"
//   "" / null     → ""
export function formatPartialDate(value) {
  if (!value) return '';
  const { year, month, day } = parseValue(value);
  if (!year) return value || '';
  const m = Number(month);
  if (!m) return year;
  const label = MONTHS[m] || '';
  if (!day) return `${label} ${year}`;
  return `${label} ${day}, ${year}`;
}

export default PartialDateInput;
